import { useEffect, useMemo, useState } from "react";


import {
  Grid,
  TextField as MUITextField,
  Skeleton,
  Typography,
} from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { AxiosResponse } from "axios";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import throttle from "lodash/throttle";
import queryString from "query-string";

import { axios } from "../api";

import type { AeronetV6ItemsResponse } from "../types";
import type { IAircraft } from "./types";
import { useExchangeRates } from "../hooks/useExchangeRates";


export interface AircraftAutocompleteProps<
T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  displayLable: any;
  isDropdownIconVisible?: boolean;
  filterActiveOnly?: boolean; // Add this optional prop
}
export function AircraftAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(props: AircraftAutocompleteProps<IAircraft, Multiple, DisableClearable, FreeSolo>) {
  const { form, onChange, isDropdownIconVisible, filterActiveOnly = false, ...restProps } = props;
  const dropdownIconVisible = isDropdownIconVisible !== undefined ? isDropdownIconVisible : true;
  
  // Create props without custom properties for fieldToAutocomplete
  const autocompleteProps = { ...restProps, form };
  
  const { setFieldValue } = form;
  const [aircrafts, setAircrafts] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState("");
  const { name } = fieldToTextField(props as any);
  const exchangeRates = useExchangeRates();

  const fetchParts = useMemo(
    () =>
      throttle((value: string, callback: Function) => {
        // Base filters that are always applied
        const filters = [
          JSON.stringify({"field":"registration","op":"!=","value":"","model":"Aircraft"}),
          JSON.stringify({"field":"registration","op":"!=","value":"None","model":"Aircraft"})
        ];
        
        // Conditionally add status filter if filterActiveOnly is true
        if (filterActiveOnly) {
          filters.push(JSON.stringify({"field":"status","op":"==","value":"0","model":"Aircraft"}));
        }

        const params = {
          filter: filters,
          q: value 
        }

        axios
          .get('/v6/aircraft', 
            {
              params,
              paramsSerializer: (params) => queryString.stringify(params),
            })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<IAircraft>>) => {
            callback(resp.data.items);
          })
          .catch((_error) => {
            callback([]);
          });
      }, 200),
    [filterActiveOnly]
  );
  
  const handleChange = (event: React.SyntheticEvent<Element, Event>, newValue: any,reason,details) => {
    if(onChange){
      const aircraft = newValue;
    onChange(event,newValue,reason,details)
    setFieldValue("aircraft", aircraft);
    setFieldValue("aircraft_id", aircraft?.id);
    setFieldValue("aircraft_registration", aircraft?.registration);
    setFieldValue("aircraft_serial_no", aircraft?.serial_no);
    setFieldValue("aircraft_model", aircraft?.model);
    if (aircraft?.delivery_date) {
      setFieldValue("delivery_date", aircraft.delivery_date);
    }            
    if (typeof aircraft === "string") {
      setFieldValue("aircraft_id", aircraft);
    } else {
      setFieldValue("aircraft_id", aircraft && aircraft.id ? aircraft.id : 0);
      setFieldValue("lines.0.aircraft_id", aircraft && aircraft.id ? aircraft.id : "");
    }
    
    }
  }

  useEffect(() => {
    let active = true;

    fetchParts(inputValue, (results: ReadonlyArray<IAircraft>) => {
      if (active) {
        let newParts: Array<string | IAircraft> = [];

        if (results) {
          newParts = [...newParts, ...results];
        }

        setAircrafts(newParts);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetchParts]);

  return (
    <>
      {exchangeRates ? (
        <MuiAutocomplete
          {...fieldToAutocomplete(autocompleteProps)}
          fullWidth
          options={aircrafts}
          autoComplete
          includeInputInList
          //@ts-ignore
          onChange={handleChange}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.registration
          }
          isOptionEqualToValue={(option, value) =>
            option.registration === value.registration
          }
          renderInput={(params: AutocompleteRenderInputParams) => (
            <MUITextField
              {...params}
              label={`${autocompleteProps?.displayLable ? autocompleteProps?.displayLable : 'Aircraft registration#'}`}
              error={autocompleteProps.form.touched[name!] && !!autocompleteProps.form.errors[name!]}
              // @ts-ignore
              helperText={autocompleteProps.form.touched[name!] && autocompleteProps.form.errors[name!]}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: !dropdownIconVisible ? null : params.InputProps?.endAdornment, // Conditionally remove or keep the dropdown icon
              }}
            />
          )}
          renderOption={(props, option, state) => {
            const matches = match(option.registration, state.inputValue);
            const aircrafts = parse(option.registration, matches);

            return (
              <li {...props}>
                <Grid container alignItems="center">
                  
                  <Grid item xs>
                    {aircrafts.map((part, index) => (
                      <span
                        key={index}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}
                    <Typography variant="body2" color="textSecondary">
                      {option.registration} ({option.id})
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      ) : (
        <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
      )}
    </>
  );
}

AircraftAutocomplete.displayName = "FormikMaterialUIPartAutocomplete";
